import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import BackofficeLayout from '../../components/backoffice/BackofficeLayout';
import StatCard from '../../components/backoffice/StatCard';
import DataTable from '../../components/backoffice/DataTable';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { propertyService } from '../../services/propertyService';
import { requestService } from '../../services/requestService';
import { testimonialService } from '../../services/testimonialService';
import { Home, FileText, Clock, TrendingUp, Eye, ArrowRight, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const Dashboard = () => {
  const [stats, setStats] = useState({
    totalProperties: 0,
    totalRequests: 0,
    newRequests: 0,
    pendingTestimonials: 0
  });
  const [recentRequests, setRecentRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);

      const [propertiesRes, requestsRes, newRequestsRes, allTestimonials] = await Promise.all([
        propertyService.getAllProperties({ limit: 1 }),
        requestService.getAllRequests({ limit: 1 }),
        requestService.getAllRequests({ statut: 'Nouveau', limit: 1 }),
        testimonialService.getAllTestimonials().catch(() => [])
      ]);

      const pendingCount = Array.isArray(allTestimonials)
        ? allTestimonials.filter(t => !t.approuve).length
        : 0;

      setStats({
        totalProperties: propertiesRes.total || 0,
        totalRequests: requestsRes.total || 0,
        newRequests: newRequestsRes.total || 0,
        pendingTestimonials: pendingCount
      });

      const recentRes = await requestService.getAllRequests({ limit: 5 });
      setRecentRequests(recentRes.data || []);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (statut) => {
    const statusConfig = {
      'Nouveau': { label: 'Nouveau', variant: 'default', className: 'bg-blue-100 text-blue-800' },
      'En cours': { label: 'En cours', variant: 'default', className: 'bg-yellow-100 text-yellow-800' },
      'Traité': { label: 'Traité', variant: 'default', className: 'bg-green-100 text-green-800' },
      'Clos': { label: 'Clos', variant: 'default', className: 'bg-slate-100 text-slate-800' }
    };

    const config = statusConfig[statut] || statusConfig['Nouveau'];
    return (
      <Badge variant={config.variant} className={config.className}>
        {config.label}
      </Badge>
    );
  };

  const requestColumns = [
    {
      header: 'Client',
      accessorKey: 'nomClient',
      cell: (row) => (
        <div>
          <div className="font-medium">{row.nomClient} {row.prenomClient || ''}</div>
          <div className="text-sm text-slate-500">{row.emailClient || '-'}</div>
        </div>
      )
    },
    {
      header: 'Type',
      accessorKey: 'typeDemande',
      cell: (row) => (
        <span className="capitalize text-sm">
          {row.typeDemande?.replace('-', ' ')}
        </span>
      )
    },
    {
      header: 'Statut',
      accessorKey: 'statut',
      cell: (row) => getStatusBadge(row.statut)
    },
    {
      header: 'Date',
      accessorKey: 'dateDemande',
      cell: (row) => {
        const dateStr = row.dateDemande;
        if (!dateStr) return <span className="text-sm text-slate-600">-</span>;
        try {
          const date = new Date(dateStr);
          if (isNaN(date.getTime())) return <span className="text-sm text-slate-600">-</span>;
          return (
            <span className="text-sm text-slate-600">
              {format(date, 'dd MMM yyyy', { locale: fr })}
            </span>
          );
        } catch {
          return <span className="text-sm text-slate-600">-</span>;
        }
      }
    },
    {
      header: 'Actions',
      cell: (row) => (
        <Button asChild variant="ghost" size="sm">
          <Link to={`/backoffice/requests`}>
            <Eye className="w-4 h-4 mr-1" />
            Voir
          </Link>
        </Button>
      ),
      className: 'text-right'
    }
  ];

  return (
    <BackofficeLayout>
      <div className="space-y-8">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Dashboard</h1>
          <p className="text-slate-600 mt-1">
            Vue d'ensemble de votre activité
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          <StatCard
            title="Total Biens"
            value={stats.totalProperties}
            icon={Home}
            description="Biens disponibles"
            loading={loading}
          />
          <StatCard
            title="Total Demandes"
            value={stats.totalRequests}
            icon={FileText}
            description="Toutes demandes"
            loading={loading}
          />
          <StatCard
            title="Nouvelles Demandes"
            value={stats.newRequests}
            icon={Clock}
            trend={stats.newRequests > 0 ? 'up' : 'down'}
            trendValue={stats.newRequests > 0 ? `${stats.newRequests} nouvelle${stats.newRequests > 1 ? 's' : ''}` : 'Aucune'}
            loading={loading}
          />
          <StatCard
            title="Témoignages en attente"
            value={stats.pendingTestimonials}
            icon={MessageSquare}
            trend={stats.pendingTestimonials > 0 ? 'up' : 'down'}
            trendValue={stats.pendingTestimonials > 0 ? `${stats.pendingTestimonials} à approuver` : 'Aucun'}
            loading={loading}
          />
        </div>

        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <CardTitle>Actions rapides</CardTitle>
            <CardDescription>
              Accédez rapidement aux fonctionnalités principales
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-3">
              <Button asChild className="bg-amber-600 hover:bg-amber-700">
                <Link to="/backoffice/properties">
                  <Home className="w-4 h-4 mr-2" />
                  Gérer les biens
                </Link>
              </Button>
              <Button asChild variant="outline">
                <Link to="/backoffice/requests">
                  <FileText className="w-4 h-4 mr-2" />
                  Voir les demandes
                </Link>
              </Button>
              <Button asChild variant="outline">
                <Link to="/backoffice/testimonials">
                  <MessageSquare className="w-4 h-4 mr-2" />
                  Gérer les témoignages
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Recent Requests */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>Dernières demandes</CardTitle>
              <CardDescription>
                Les 5 dernières demandes reçues
              </CardDescription>
            </div>
            <Button asChild variant="outline" size="sm">
              <Link to="/backoffice/requests">
                Voir tout
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </Button>
          </CardHeader>
          <CardContent>
            <DataTable
              columns={requestColumns}
              data={recentRequests}
              loading={loading}
              emptyMessage="Aucune demande récente"
            />
          </CardContent>
        </Card>
      </div>
    </BackofficeLayout>
  );
};

export default Dashboard;
